import type { ListBuilderConfiguration } from '../../types';
import { allLicensesList } from './lists/all-licenses';
import { badStandingList } from './lists/bad-standing';
import { confirmedList } from './lists/confirmed';
import { expiredList } from './lists/expired';
import { expiringSoonList } from './lists/expiring-soon';
import { falseBlockList } from './lists/false-block';
import { linkEndedList } from './lists/link-ended';
import { noActiveLicenseList } from './lists/no-active-license';
import { notUsableList } from './lists/not-usable';
import { staleRenewalList } from './lists/stale-renewal';
import { supersededPermitList } from './lists/superseded-permit';
import { unconfirmedClientsList } from './lists/unconfirmed-clients';
import { unmatchedList } from './lists/unmatched';

/**
 * "Current Licenses" list configuration (PD-13891).
 *
 * This is the tenant-local client_license surface — backed by GET /api/licenses
 * (LicensesService.findAll), which no longer issues cross-DB subqueries against
 * workd_mission_control on the hydrated query (the registrant name / file-count
 * DISPLAY values are still overlaid via a batched post-query fetch).
 *
 * Every tab here filters on LOCAL columns/tables only:
 * 1. Expiring Soon - renewal queue for licenses approaching expiration
 * 2. Expired - licenses that have lapsed
 * 3. Adverse Action - licenses matched to a registry record under a board
 *    action (tenant-local mirrored `registry_status` — PD-13949/PD-14745),
 *    ranked by the owning client's most recent sale
 * 4. Under Review - truly unmatched licenses with no candidate match
 * 5. Confirmed - audit trail of confirmed registry matches
 * 6. All Licenses - unfiltered view for ad-hoc searching
 *
 * REMOVED (PD-13891): "Pending Match" (relocated to the dedicated Pending
 * Matches surface, /licenses/matches), and the two registry-derived tabs
 * "Recently Changed" (lm_registry_changes) + "Discrepancies" (lm_registry) —
 * both relied on cross-DB subqueries that re-trigger the getMany hydration bug.
 * Re-sourcing those two without the cross-DB coupling is a deferred follow-up.
 *
 * REMOVED: "Has Disciplinary Action" (`default-licenses-at-risk`) — counted
 * tenant-local `license_disciplinary_action` records and overlapped
 * confusingly with Adverse Action. The population stays reachable via the
 * `licenseHasDisciplinary` sidebar filter (still in activeFilters below).
 */
export const LICENSE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		expiringSoonList,
		expiredList,
		unconfirmedClientsList,
		noActiveLicenseList,
		badStandingList,
		notUsableList,
		falseBlockList,
		staleRenewalList,
		supersededPermitList,
		linkEndedList,
		unmatchedList,
		confirmedList,
		allLicensesList,
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'licenseKeywords',
			'licenseStatus',
			'licenseType',
			// PD-15539: the merged 'State' filter is retired — two separately
			// labelled concepts replace it.
			'licenseIssuingState',
			'licenseClientLocationState',
			'licenseClient',
			'licenseClientLeadOrCustomer',
			'licenseSalesRep',
			'licenseExpirationDateRange',
			'licenseHasDisciplinary',
			'licenseBoardFlagged',
			'licenseRegistryStanding',
			'licenseLinkEnded',
			'licenseCanOrder',
			'licenseMatchStatus',
			'licenseMatchConfidence',
			// PD-13891: licenseRecentlyChanged + licenseHasRegistryDiscrepancy
			// removed from the tray. Both resolve to cross-DB subqueries against
			// workd_mission_control that re-trigger the getMany hydration bug, so
			// they must not be selectable on this tenant-local surface.
		],
		defaultFilters: {},
	},
	// Config-level named sorts (resolvable via a list's `defaultSortId`).
	// `recent-sale-desc` is the Bad Standing tab's default order (PD-13949):
	// the owning client's most recent sale date, newest first — actively-
	// buying clients holding a dead license are the biggest compliance risk.
	// `recentSaleDate` maps to the `license_recentSale_sort` SELECT alias in
	// LicensesService.findAll (tenant-local client_sales subquery).
	tableSorts: [
		{
			id: 'recent-sale-desc',
			// The "(Newest First)" direction indicator is REQUIRED in the name:
			// mergeSortOptions adds indicator-less tableSorts as rewritten
			// asc/desc pairs with suffixed ids, which would break the sort
			// dropdown's selected-option lookup for this list's defaultSortId.
			name: 'Last Sale (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'recentSaleDate',
					objectTable: 'client_license',
				},
				{
					direction: 'ASC',
					fieldName: 'expirationDate',
					objectTable: 'client_license',
				},
			],
		},
		// The Issued / Located column carries two facts, so its header is not
		// clickable; the client location order is offered here.
		{
			id: 'client-location-asc',
			name: 'Client Location (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'clientLocationState',
					objectTable: 'client_license',
				},
				{
					direction: 'ASC',
					fieldName: 'clientName',
					objectTable: 'client_license',
				},
			],
		},
		// The Sales column stacks the 90-day and 30-day figures, so the two
		// orders live here, as the Clients list keeps its revenue sorts. The
		// "(High to Low)" indicator is required for the same reason as above.
		{
			id: 'sales-90-desc',
			name: 'Sales 90 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'clientRevenue90',
					objectTable: 'client_license',
				},
				{
					direction: 'ASC',
					fieldName: 'clientName',
					objectTable: 'client_license',
				},
			],
		},
		{
			id: 'sales-30-desc',
			name: 'Sales 30 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'clientRevenue30',
					objectTable: 'client_license',
				},
				{
					direction: 'ASC',
					fieldName: 'clientName',
					objectTable: 'client_license',
				},
			],
		},
	],
	objects: [
		{
			baseUrl: '/licenses/:id',
			objectLabel: 'License',
			objectTable: 'client_license',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'licenseNumber', fieldLabel: 'License #' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'expirationDate', fieldLabel: 'Expiration Date' },
				{ fieldName: 'issueDate', fieldLabel: 'Issue Date' },
				// PD-15539: the stored column keeps its own name here (it is what the
				// licence editor writes), but the list's two state COLUMNS render the
				// derived fields below — the board that issued the licence and where
				// the client sits — which are never merged.
				{ fieldName: 'issuingState', fieldLabel: 'Issuing State (Stored)' },
				{ fieldName: 'issuingStateCode', fieldLabel: 'Issuing State' },
				{ fieldName: 'clientLocationState', fieldLabel: 'Client Location' },
				{ fieldName: 'issuingAuthority', fieldLabel: 'Issuing Authority' },
				{ fieldName: 'hasDisciplinaryAction', fieldLabel: 'Has Disciplinary' },
				{ fieldName: 'fileCount', fieldLabel: 'Files' },
				{ fieldName: 'clientName', fieldLabel: 'Client' },
				{ fieldName: 'clientLeadOrCustomer', fieldLabel: 'Lead/Customer' },
				{ fieldName: 'clientStreetAddress', fieldLabel: 'Street Address' },
				{ fieldName: 'clientCityStateZip', fieldLabel: 'City/State/Zip' },
			],
		},
	],
};
